import { useState } from 'react';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

// Types
import type { Dispatch, SetStateAction } from 'react';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import type { SortingState } from '@tanstack/react-table';
import type { DataGroupGlobalTableAnalyticsType } from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics/types';
import type { GroupGlobalTableConfigType } from '@/lib/analyticsGroup/tables/groupGlobalTableConfig.type';
import type {
    GroupGlobalTableProps,
    PeriodSelectedType
} from '@/components/pages/analyze-group/tables/GroupGlobalTable';
import type useTableColumnState from '@/utils/hooks/table/useTableColumnState';

// Components
import CompoundTable from '@/components/base/Table/CompoundTable';
import TableBodyPlaceholder from '@/components/pages/analyze-group/tables/GroupGlobalTable/TableBodyPlaceholder';
import ActionBar from '@/components/pages/analyze-group/tables/GroupGlobalTable/ActionBar';

type ColumnVisibilityStateType = Omit<GroupGlobalTableConfigType['columnsVisibility'], 'avatar'>;

export type GroupGlobalTableContentProps = Pick<
    GroupGlobalTableProps,
    'config' | 'userDataFormat'
> & {
    periodSelected: PeriodSelectedType;
    activeGroup: SpGroupType;
    groupAnalytics: Prettify<DataGroupGlobalTableAnalyticsType>;
    isDataLoading: boolean;
    columnVisibility: ColumnVisibilityStateType;
    columnOrder: ReturnType<typeof useTableColumnState>['columnOrder'];
    setColumnOrder: ReturnType<typeof useTableColumnState>['setColumnOrder'];
    setColumnVisibility: Dispatch<SetStateAction<ColumnVisibilityStateType>>;
};

const GroupGlobalTableContent = ({
    activeGroup,
    groupAnalytics,
    isDataLoading,
    config,
    userDataFormat,
    periodSelected,
    columnOrder,
    columnVisibility,
    setColumnOrder,
    setColumnVisibility
}: GroupGlobalTableContentProps) => {
    const columns = config.columns;

    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data: groupAnalytics ?? [],
        columns,
        meta: { ...config.meta, ...userDataFormat, periodSelected },
        state: { sorting, columnVisibility, columnOrder },
        onColumnOrderChange: setColumnOrder,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility
    });

    return (
        <div className="space-y-3">
            <ActionBar
                table={table}
                periodSelected={periodSelected}
                userDataFormat={userDataFormat}
                setColumnVisibility={setColumnVisibility}
            />
            <CompoundTable table={table} className="h-auto max-h-[calc(100vh-220px)]">
                <CompoundTable.Header />
                {isDataLoading ? (
                    <TableBodyPlaceholder nbRows={activeGroup?.social_profiles?.length + 1} />
                ) : (
                    <CompoundTable.Body />
                )}
            </CompoundTable>
        </div>
    );
};

export default GroupGlobalTableContent;
